import React, { useEffect } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SellIcon from '@mui/icons-material/Sell';
import ChatIcon from '@mui/icons-material/Chat';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import SchoolIcon from '@mui/icons-material/School';
import { useNavigate } from 'react-router-dom';

export default function HowItWorks() {
  const navigate = useNavigate();
  useEffect(() => {
    window.handleSearch = (searchValue) => {
      if (searchValue && searchValue.trim()) {
        navigate(`/listings?search=${encodeURIComponent(searchValue.trim())}`);
      }
    };
    return () => { window.handleSearch = undefined; };
  }, [navigate]);

  const steps = [
    {
      icon: <SearchIcon />,
      title: 'Find Your Textbook',
      description: 'Search through thousands of textbooks available on campus. Filter by course, condition, and price to find exactly what you need.',
    },
    {
      icon: <SellIcon />,
      title: 'List Your Books',
      description: 'Sell your used textbooks easily. Take photos, set your price, and connect with fellow students looking for your books.',
    },
    {
      icon: <ChatIcon />,
      title: 'Connect Directly',
      description: 'Message buyers and sellers directly through our secure chat system. No middlemen, no hidden fees.',
    },
    {
      icon: <PaymentIcon />,
      title: 'Safe Online Transactions',
      description: 'Complete secure online payments through our integrated payment system. Provide your 4-digit approval code when you receive your textbook.',
    },
  ];

  const features = [
    {
      icon: <SchoolIcon />,
      title: 'Campus Verified',
      description: 'All users are verified with their school email address, ensuring a safe and trusted community.',
    },
    {
      icon: <SecurityIcon />,
      title: 'Secure Platform',
      description: 'Your personal information is protected. We never share your data with third parties.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#E6F0F9', pt: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 'bold', 
            color: '#1a1a1a',
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            How bookloop Works
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            fontSize: { xs: '1.1rem', md: '1.3rem' }
          }}>
            The easiest way to buy and sell textbooks on campus. Connect with fellow students, save money, and make the most of your academic journey.
          </Typography>
        </Box>

        {/* How It Works Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center', 
            mb: 6,
            fontWeight: 'bold',
            color: '#1a1a1a'
          }}>
            Simple 4-Step Process
          </Typography>
          
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8,
                  },
                }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#6B89FF',
                      color: 'white',
                      margin: '0 auto 16px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '40px',
                      },
                    }}>
                      {step.icon}
                    </Box>
                    <Typography variant="h6" component="h3" sx={{ 
                      fontWeight: 'bold',
                      mb: 2,
                      color: '#1a1a1a'
                    }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center', 
            mb: 6,
            fontWeight: 'bold',
            color: '#1a1a1a'
          }}>
            Why Choose bookloop?
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper elevation={2} sx={{ p: 4, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      backgroundColor: '#6B89FF',
                      color: 'white',
                      mr: 3,
                      '& .MuiSvgIcon-root': {
                        fontSize: '30px',
                      },
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h3" sx={{ 
                      fontWeight: 'bold',
                      color: '#1a1a1a'
                    }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Online Payment Benefits */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center', 
            mb: 6,
            fontWeight: 'bold',
            color: '#1a1a1a'
          }}>
            Why Pay Online?
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Box sx={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  backgroundColor: '#6B89FF', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  💳
                </Box>
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#1a1a1a'
                }}>
                  No Cash Needed
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  No need to bring cash when you meet the seller. All payments are processed securely online through our platform.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Box sx={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  backgroundColor: '#6B89FF', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  🛡️
                </Box>
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#1a1a1a'
                }}>
                  Seller Protection
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  The seller only gets paid after you receive your item and provide them with your 4-digit approval code. This ensures you get what you paid for before the seller receives payment.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Box sx={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  backgroundColor: '#6B89FF', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  🔒
                </Box>
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#1a1a1a'
                }}>
                  Safe & Refundable
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  Your money is held safely and you can get a refund if something goes wrong. We protect both buyers and sellers.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ 
          textAlign: 'center', 
          backgroundColor: '#6B89FF', 
          color: 'white',
          borderRadius: 4,
          p: 6,
          mb: 4
        }}>
          <Typography variant="h4" component="h2" sx={{ 
            fontWeight: 'bold',
            mb: 2
          }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join thousands of students who are already saving money on textbooks
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box
              component="button"
              sx={{
                backgroundColor: 'white',
                color: '#6B89FF',
                border: 'none',
                borderRadius: '14px',
                px: 4,
                py: 2,
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
              onClick={() => window.location.href = '/listings'}
            >
              Find Textbooks
            </Box>
            <Box
              component="button"
              sx={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '14px',
                px: 4,
                py: 2,
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
              onClick={() => window.location.href = '/my-listings'}
            >
              Sell Your Books
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
} 